import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { useMemo } from "react";
import { ListingGroup } from "~/components/layout/ListingGroup";
import { ListingItem } from "~/components/layout/ListingItem";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { Only } from "~/components/permission/Only";
import type { Allergy } from "~/entities/medical/allergy";
import type { Condition } from "~/entities/medical/condition";
import type { Medication } from "~/entities/medical/medication";
import { useDismiss } from "~/providers/DismissProvider";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";
import { useProfile } from "~/providers/ProfileProvider";
import type { AllergyListingItem } from "../health-profile/allergies";
import type { ConditionListingItem } from "../health-profile/conditions";
import type { MedicationListingItem } from "../health-profile/medications";

export default function Index() {
    const { instructions, contacts, profile } = useProfile();
    const { handleDismiss } = useDismiss();
    const { listMedical } = useMedical();
    const { findOption } = useOptions();

    const emergency_contacts = useMemo(() => contacts.filter(({ emergency_contact }) => emergency_contact), [contacts]);

    const allergy_items: AllergyListingItem[] = useMemo(() => {
        return listMedical(MedicalType.allergy).map((a: Allergy) => ({
            id: a.id as string,
            title: findOption(OptionType.allergies, a.option_id)?.title as string,
            emergency: a.flag,
            level: a.level,
        }));
    }, [findOption, listMedical]);

    const condition_items: ConditionListingItem[] = useMemo(() => {
        return listMedical(MedicalType.condition).map((c: Condition) => ({
            id: c.id as string,
            title: findOption(OptionType.conditions, c.option_id)?.title as string,
            primary: c.primary,
            confirmed: c.confirmed,
        }));
    }, [findOption, listMedical]);

    const medication_items: MedicationListingItem[] = useMemo(() => {
        return listMedical(MedicalType.medication)
            .map((m: Medication) => ({
                id: m.id as string,
                title: findOption(OptionType.medications, m.option_id)?.title as string,
                prescribed: m.prescribed,
                current: m.end_date === null,
            }))
            .filter((i) => i.current);
    }, [findOption, listMedical]);

    return (
        <FullPageModal title="Emergency information" onRequestClose={handleDismiss} withDone asRoute>
            <Only when={instructions}>
                <ListingGroup
                    title="Instructions"
                    items={instructions.map(({ id, description }) => ({
                        key: id,
                        title: description,
                        hideChevron: true,
                    }))}
                />
            </Only>
            <Only when={emergency_contacts}>
                <ListingGroup
                    title="Emergency contacts"
                    items={emergency_contacts.map(({ id, name }) => ({
                        key: id,
                        title: name,
                        href: {
                            pathname: "/emergency-information/[contact_id]/show",
                            params: { contact_id: id },
                        },
                    }))}
                />
            </Only>
            <Only when={condition_items}>
                <ListingGroup
                    title="Medical conditions"
                    items={condition_items.map(({ id, title, primary }) => ({
                        key: id,
                        title,
                        badge: primary ? "Primary condition" : null,
                        hideChevron: true,
                    }))}
                />
            </Only>
            <Only when={allergy_items}>
                <ListingGroup
                    title="Allergies"
                    items={allergy_items.map(({ id, title, emergency }) => ({
                        key: id,
                        title,
                        badge: emergency ? "Emergency reaction" : null,
                        badgeVariant: "danger",
                        hideChevron: true,
                    }))}
                />
            </Only>
            <Only when={medication_items}>
                <ListingGroup
                    title="Medication"
                    items={medication_items.map(({ id, title, prescribed }) => ({
                        key: id,
                        title,
                        badge: prescribed ? "Prescribed" : null,
                        hideChevron: true,
                    }))}
                />
            </Only>
            <Only when={profile?.blood_type}>
                <ListingGroup title="Blood type">
                    <ListingItem title={profile?.blood_type ?? ""} last hideChevron />
                </ListingGroup>
            </Only>
        </FullPageModal>
    );
}
